import { useCallback, useEffect, useRef, useState } from 'react';
import { QueueTable, useQueueGrid } from '@components/QueueTable';
import { StatusTabs, type TabDef, type TabKey } from '@components/StatusTabs';
import { EventDrawer } from '@components/EventDrawer';
import { openEvent } from '@lib/open-mode';
import { cn } from '@/lib/cn';
import { createOutbound, currentClient, getRoster } from '@lib/care-api';
import { useEventQueue } from '@lib/useEventQueue';
import { fmtMemberId } from '@lib/member-id';
import { fmtDate, statusLabel, type EventStatus, type RosterResp } from '@lib/care-model';

/**
 * ConsentChase - Flow B (proactive outbound). Two sub-views:
 *  - Roster: the read-only 26fobt no-consent worklist. "Start chase" mints an outbound event (the API
 *    reads 26fobt read-only; nothing is written back). Members already in the queue link to the event.
 *  - Active chases: the outbound consent-chase events, worked through the shared drawer (call, capture
 *    consent / refused / unreachable -> emits a consent-update handoff to the Portal; never to 26fobt).
 */

const OUTBOUND_TABS: TabDef[] = [
  { key: 'new', label: 'New' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'actioned', label: 'Actioned' },
  { key: 'in_review', label: 'In review' },
  { key: 'all', label: 'All' },
];

type View = 'roster' | 'active';

export function ConsentChase() {
  const [view, setView] = useState<View>('roster');
  const [tab, setTab] = useState<TabKey>('new');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [flash, setFlash] = useState('');
  const [minting, setMinting] = useState('');

  // Full outbound list (unfiltered by status) drives both the active table and the roster dedupe map.
  const { data, loading, reload } = useEventQueue({ direction: 'outbound', status: undefined, q });
  const memberToEvent = new Map<string, string>();
  for (const it of data?.items ?? []) {
    if (it.memberId && !memberToEvent.has(it.memberId)) memberToEvent.set(it.memberId, it.eventId);
  }
  const activeItems = (data?.items ?? []).filter((i) => tab === 'all' || i.status === tab);
  // Row 356: the shared grid for the active chases (the server's order until she sorts).
  const activeGrid = useQueueGrid({ table: 'consent-active', items: activeItems, slicers: ['channel', 'status', 'handledBy'] });

  // Roster (loaded lazily on the roster view + when search changes).
  const [roster, setRoster] = useState<RosterResp | null>(null);
  const [rosterLoading, setRosterLoading] = useState(false);

  const loadRoster = useCallback(async () => {
    setRosterLoading(true);
    try {
      const r = await getRoster({ q, limit: 300 });
      setRoster(r);
    } catch {
      setRoster({ client: currentClient(), available: false, consentColumn: null, count: 0, members: [] });
    } finally {
      setRosterLoading(false);
    }
  }, [q]);

  useEffect(() => {
    if (view === 'roster') void loadRoster();
  }, [view, loadRoster]);

  const startChase = async (memberId: string, consentState: 'none' | 'unknown') => {
    const existing = memberToEvent.get(memberId);
    if (existing) {
      openEvent(existing, setSelected); // the open-cases switch (William, 2 Oct 2026)
      return;
    }
    setMinting(memberId);
    setFlash('');
    try {
      const r = await createOutbound(memberId, consentState);
      const created = (r.data as { event?: { eventId: string } }).event;
      if (r.ok && created) {
        setFlash(`Chase started for #${fmtMemberId(memberId)}.`);
        // Await the refresh so the dedupe map (memberToEvent) reflects the new event before `minting`
        // clears - a second click in the gap then reads "Open" instead of minting a duplicate.
        await reload();
        // A chase just started opens HERE, in the side panel, whatever the switch says: it opens after
        // two round trips, outside the click, where a browser may block a new window.
        setSelected(created.eventId);
      } else {
        setFlash(`Could not start chase (HTTP ${r.status}).`);
      }
    } finally {
      setMinting('');
    }
  };

  /** View follows the work: when a drawer mutation moved the selected chase into another status
   * bucket (e.g. Claim: new -> in_progress), switch the Active-chases tab to follow it so the row
   * stays visible instead of silently vanishing from the current tab.
   * Latest-value refs: the drawer's mutate captures this callback at click time, and the mutation
   * round-trip can land AFTER the operator switched tabs or events - the guards must read the
   * CURRENT tab/selected, not the click-time closure, or a late response yanks the view. */
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const onDrawerChanged = (updated?: { eventId: string; status: string }) => {
    reload();
    if (view === 'roster') void loadRoster();
    if (!updated || updated.eventId !== selectedRef.current) return;
    const curTab = tabRef.current;
    if (curTab === 'all' || updated.status === curTab) return;
    if (!OUTBOUND_TABS.some((t) => t.key === updated.status)) return;
    setTab(updated.status as TabKey);
    setFlash(`Moved to ${statusLabel(updated.status as EventStatus)} - tab switched to follow it.`);
  };

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">Member Care · Flow B</div>
        <h1 className="section-title">
          Consent <em>chase</em>
        </h1>
        <div className="section-rule" />
        <p className="section-lede">
          Proactive outreach to members with no consent on file. Missing consent never holds the result
          letter or PCP fax; this is value-add background research. Outcomes are reported to the Portal
          as handoffs, never written to 26fobt.
        </p>
      </header>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" className={cn('chip', view === 'roster' && 'selected')} onClick={() => setView('roster')}>
          No-consent roster
        </button>
        <button type="button" className={cn('chip', view === 'active' && 'selected')} onClick={() => setView('active')}>
          Active chases{data?.items.length ? ` (${data.items.length})` : ''}
        </button>
        <input
          className="text-input"
          type="search"
          aria-label="Search consent-chase roster and events"
          style={{ marginLeft: 'auto', maxWidth: 320 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name / member ID / phone"
        />
      </div>

      {flash && <div className="note" style={{ marginBottom: 14 }}>{flash}</div>}

      {view === 'roster' ? (
        <>
          {roster && !roster.available && (
            <div className="note danger" style={{ marginBottom: 14 }}>
              <b>26fobt reference unavailable.</b> The roster could not be read right now.
            </div>
          )}
          {roster && roster.available && roster.consentColumn === null && (
            <div className="note warn" style={{ marginBottom: 14 }}>
              <b>Consent state unknown.</b> 26fobt does not carry a consent column, so the full roster is
              shown flagged consent <span className="mono">unknown</span>. Confirm before recording an
              outcome.
            </div>
          )}
          <div className="card card-flush">
            <table className="dt">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Language</th>
                  <th>Last activity</th>
                  <th>Consent</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rosterLoading && (
                  <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>Loading roster...</td></tr>
                )}
                {!rosterLoading && (roster?.members.length ?? 0) === 0 && (
                  <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>No members in the no-consent worklist.</td></tr>
                )}
                {roster?.members.map((mem) => {
                  const inQueue = memberToEvent.has(mem.memberId);
                  return (
                    <tr key={mem.memberId}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {mem.fullName} <span className="muted">#{fmtMemberId(mem.memberId)}</span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{[mem.phone1, mem.phone2].filter(Boolean).join(' / ') || <span className="muted">-</span>}</td>
                      <td>{mem.email || <span className="muted">-</span>}</td>
                      <td>{mem.language || <span className="muted">-</span>}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(mem.lastActivity) || <span className="muted">-</span>}</td>
                      <td><span className={cn('pill', mem.consentState === 'unknown' && 'pill-warning')}>{mem.consentState}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className={cn('btn', 'btn-sm', !inQueue && 'btn-teal')}
                          disabled={minting === mem.memberId}
                          onClick={() => void startChase(mem.memberId, mem.consentState)}
                        >
                          {inQueue ? 'Open' : 'Start chase'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: 14 }}>
            <StatusTabs tabs={OUTBOUND_TABS} active={tab} totals={data?.totals} onSelect={setTab} />
          </div>
          <QueueTable
            grid={activeGrid}
            selectedId={selected}
            loading={loading}
            onSelect={(id) => openEvent(id, setSelected)}
            emptyLabel="No active consent-chase events in this view."
          />
        </>
      )}

      {selected && (
        <EventDrawer eventId={selected} onClose={() => setSelected(null)} onChanged={onDrawerChanged} />
      )}
    </div>
  );
}
